import React from 'react';

import { useLocation, NavLink } from 'react-router-dom';
import { useQuery } from 'react-query';

import classNames from 'classnames';
import myLibraryIcon from '../../../assets/images/nav/my-library.svg';
import myVideosIcon from '../../../assets/images/nav/my-videos.svg';
import settingsIcon from '../../../assets/images/nav/profile.svg';
import trashIcon from '../../../assets/images/nav/trash-bin.svg';
import myLibraryActiveIcon from '../../../assets/images/nav/my-library-active.svg';
import trashActiveIcon from '../../../assets/images/nav/trash-bin-active.svg';
import profileActiveIcon from '../../../assets/images/nav/profile-active.svg';
import myVideosActiveIcon from '../../../assets/images/nav/my-videos-active.svg';
import trainingPathsIcon from '../../../assets/images/nav/training-paths.svg';
import trainingPathsActiveIcon from '../../../assets/images/nav/training-paths-active.svg';
import templatesIcon from '../../../assets/images/nav/templates.svg';
import templatesActiveIcon from '../../../assets/images/nav/templates-active.svg';
import superAdminIcon from '../../../assets/images/nav/super-admin.svg';
import superAdminActiveIcon from '../../../assets/images/nav/super-admin-active.svg';
import { useUiStore } from '../../../store';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import UserService from '../../../services/UserService';
import { isTrainingPathsEnabledForOrg, ORG_ROLES, USER_ROLES } from '../../../constants/main';

import classes from './styles.module.scss';

const baseLinks = [
  {
    title: 'My library',
    path: '/my-library',
    icon: myLibraryIcon,
    activeIcon: myLibraryActiveIcon,
  },
  {
    title: 'My Videos',
    path: '/my-videos',
    icon: myVideosIcon,
    activeIcon: myVideosActiveIcon,
  },
  {
    title: 'Templates',
    path: '/templates',
    icon: templatesIcon,
    activeIcon: templatesActiveIcon,
  },
  {
    title: 'Trash',
    path: '/trash-bin',
    icon: trashIcon,
    activeIcon: trashActiveIcon,
  },
];

const trainingPathsLink = {
  title: 'Training Paths',
  path: '/training-paths',
  icon: trainingPathsIcon,
  activeIcon: trainingPathsActiveIcon,
};

const settingsLink = {
  title: 'Settings',
  path: '/organization-settings',
  icon: settingsIcon,
  activeIcon: profileActiveIcon,
};

const superAdminLink = {
  title: 'Super Admin',
  path: '/super-admin',
  icon: superAdminIcon,
  activeIcon: superAdminActiveIcon,
};

const helpCenterLink = {
  title: 'Help center',
  path: '/super-admin/help-center',
  iconText: '?',
};

export default function NavBar() {
  const location = useLocation();
  const currentPath = location.pathname;

  const isMobile = useCheckMobileScreen();

  const isSidebarCollapsed = useUiStore((state) => state.isSidebarCollapsed);
  const setContactUsOpen = useUiStore((state) => state.setContactUsOpen);

  // Get current user to check if they can access Settings
  const { data: currentUser } = useQuery('me', UserService.getMe);
  
  // Check if user is org owner
  const canAccessOrgSettings =
    currentUser?.orgRole?.access === ORG_ROLES.OWNER;

  // Build links array conditionally
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;

  const isImpersonating = currentUser?.impersonation?.isImpersonating;
  const showExtendedLinks = !isSuperAdmin || isImpersonating;

  const links = showExtendedLinks ? [...baseLinks] : [];

  if (showExtendedLinks && isTrainingPathsEnabledForOrg(currentUser)) {
    const trashIndex = links.findIndex((link) => link.path === '/trash-bin');
    const insertionIndex = trashIndex === -1 ? links.length : trashIndex;
    links.splice(insertionIndex, 0, trainingPathsLink);
  }

  if (showExtendedLinks && canAccessOrgSettings) {
    const trashIndex = links.findIndex((link) => link.path === '/trash-bin');
    const insertionIndex =
      trashIndex === -1 ? links.length : Math.max(trashIndex, 1);
    links.splice(insertionIndex, 0, settingsLink);
  }

  if (isSuperAdmin) {
    links.unshift(superAdminLink);
    links.splice(1, 0, helpCenterLink);
  }

  if (showExtendedLinks) {
    links.push({
      title: 'Help',
      onClick: () => setContactUsOpen(true),
      iconText: '?',
    });
  }

  // Exclamation mark icon SVG for impersonation
  const exclamationIcon = (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <ul
      className={classNames(classes.NavBar, {
        [classes.collapsed]: isSidebarCollapsed && !isMobile,
      })}
    >
      {links.map(({ title, path, icon, activeIcon, iconText, onClick }) => {
        const isActiveLink = path ? currentPath.startsWith(path) : false;
        const isSuperAdminLink = path === '/super-admin';
        const shouldShowImpersonationStyle = isSuperAdminLink && isImpersonating;
        const LinkComponent = path ? NavLink : 'a';
        const linkProps = path
          ? {
              to: path,
              className: ({ isActive }) =>
                classNames({
                  [classes.active]: isActive && !shouldShowImpersonationStyle,
                  [classes.impersonating]: shouldShowImpersonationStyle,
                }),
            }
          : {
              href: '#',
              className: classNames({
                [classes.impersonating]: false,
              }),
              onClick: (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (onClick) onClick();
              },
            };

        let iconNode = null;
        if (shouldShowImpersonationStyle) {
          iconNode = exclamationIcon;
        } else if (iconText) {
          iconNode = <span className={classes.iconText}>{iconText}</span>;
        } else {
          iconNode = <img src={isActiveLink ? activeIcon : icon} alt={title} />;
        }
        
        return (
          <LinkComponent
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...linkProps}
            onClick={(event) => {
              event.stopPropagation();
              if (!path && linkProps.onClick) linkProps.onClick(event);
            }}
            key={path || title}
          >
            <li>
              <div
                className={classNames(classes.iconContainer, [
                  classes[title.split(' ').join('')],
                ], {
                  [classes.impersonatingIcon]: shouldShowImpersonationStyle,
                })}
              >
                {iconNode}
              </div>
              <span className={classes.title}>{title}</span>
            </li>
          </LinkComponent>
        );
      })}
    </ul>
  );
}
